import { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Package, Clock, Fuel, Wrench, TrendingUp, TrendingDown, Minus, Loader2, BarChart3, Gauge, AlertTriangle, Car, Bike, Calendar, Check, Trophy, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { usePlatform, UNIFIED } from "../lib/ridersPlatform";
import { RiderPlatformSwitcher } from "./RiderPlatformSwitcher";
import { toast } from "sonner";

const AR_MONTHS = ["", "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const monthLabel = (mk) => {
  const [y, m] = (mk || "").split("-");
  return m ? `${AR_MONTHS[parseInt(m, 10)]} ${String(y).slice(2)}` : mk;
};
const fmt = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(n || 0);

const Delta = ({ curr, prev, invert }) => {
  if (prev === undefined || prev === null) return null;
  const diff = (curr || 0) - (prev || 0);
  if (Math.abs(diff) < 0.01) return <span className="inline-flex items-center gap-0.5 text-slate-400 text-xs"><Minus className="h-3 w-3" /> ثابت</span>;
  const good = invert ? diff < 0 : diff > 0;
  const pct = prev ? Math.abs(diff / prev) * 100 : 100;
  const Icon = diff > 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${good ? "text-emerald-600" : "text-rose-500"}`}>
      <Icon className="h-3 w-3" /> {fmt(pct)}%
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, unit, tone, curr, prev, invert }) => (
  <div data-testid={`compare-stat-${label}`} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${tone}`}><Icon className="h-4.5 w-4.5" /></div>
      <Delta curr={curr} prev={prev} invert={invert} />
    </div>
    <div className="mt-2 text-2xl font-heading font-extrabold text-slate-900 font-mono-nums">{fmt(value)}<span className="text-sm font-bold text-slate-400 mr-1">{unit}</span></div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-sm font-heading font-bold text-slate-800 mb-3">{title}</div>
    <div style={{ width: "100%", height: 240 }} dir="ltr">
      <ResponsiveContainer>{children}</ResponsiveContainer>
    </div>
  </div>
);

const FLAG_META = {
  over: { cls: "text-rose-700 bg-rose-50 border-rose-200", label: "استهلاك زائد", icon: TrendingUp },
  under: { cls: "text-sky-700 bg-sky-50 border-sky-200", label: "أقل من المتوقع", icon: TrendingDown },
  ok: { cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "ضمن المعيار", icon: Minus },
};
const FlagBadge = ({ flag, dev }) => {
  const m = FLAG_META[flag] || FLAG_META.ok;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
      <Icon className="h-3 w-3" /> {dev > 0 ? "+" : ""}{fmt(dev)}%
    </span>
  );
};

const RankList = ({ title, rows, tone, empty, onPick }) => (
  <div className="flex-1 min-w-0">
    <div className={`text-xs font-bold mb-1.5 ${tone === "best" ? "text-emerald-700" : "text-rose-600"}`}>{title}</div>
    {rows.length === 0 ? (
      <p className="text-[11px] text-slate-400 py-2">{empty}</p>
    ) : (
      <ol className="space-y-1">
        {rows.map((r, i) => (
          <li key={r.rider_id}>
            <button onClick={() => onPick(r)} data-testid={`daily-${tone}-${r.rider_id}`}
              className="w-full flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1.5 hover:bg-slate-100 transition-colors text-right">
              <span className="w-5 h-5 shrink-0 rounded-full bg-white text-[10px] font-bold text-slate-500 flex items-center justify-center font-mono-nums">{i + 1}</span>
              <span className="flex-1 min-w-0 truncate text-xs font-medium text-slate-700" title={r.name}>{r.name}</span>
              <span className={`font-mono-nums text-sm font-bold ${r.met ? "text-emerald-600" : "text-slate-700"}`}>{fmt(r.orders)}</span>
              {r.met ? <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <span className="w-3.5 shrink-0" />}
            </button>
          </li>
        ))}
      </ol>
    )}
  </div>
);

const RiderTargetCardDialog = ({ rider, onClose }) => {
  const r = rider || {};
  const tier = r.monthly_target > 0
    ? (r.month_orders >= r.monthly_target ? "monthly" : r.month_orders >= r.expected ? "above" : r.month_orders >= r.expected - 5 ? "on" : "under")
    : null;
  const grad = tier === "monthly" ? "from-[#e0a458] via-[#b9752f] to-[#7c4a22]"
    : tier === "above" ? "from-emerald-400 via-green-600 to-teal-600"
    : tier === "on" ? "from-amber-400 via-yellow-500 to-amber-500"
    : tier === "under" ? "from-rose-500 via-red-600 to-rose-700"
    : "from-slate-600 via-slate-700 to-slate-800";
  return (
    <Dialog open={!!rider} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm font-body" dir="rtl" data-testid="rider-target-card">
        <DialogHeader><DialogTitle className="font-heading text-base">{r.name}</DialogTitle></DialogHeader>
        <div className={`relative overflow-hidden rounded-3xl p-5 text-white shadow-lg bg-gradient-to-br ${grad}`}>
          <Trophy className="pointer-events-none absolute -top-3 -left-3 h-20 w-20 rotate-12 text-white/10" />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/25 px-2.5 py-1 text-[11px] font-bold backdrop-blur-sm">
            {r.vehicle_type === "سيارة" ? <Car className="h-3.5 w-3.5" /> : <Bike className="h-3.5 w-3.5" />} {r.vehicle_type || "—"}
          </span>
          <p className="mt-3 text-xs text-white/90">إجمالي الطلبات التي حقّقها المندوب</p>
          <p className="font-heading text-5xl font-extrabold font-mono-nums leading-none mt-1 drop-shadow-md" dir="ltr">{fmt(r.month_orders)}</p>
          {r.monthly_target > 0 ? (
            <div className="mt-3 space-y-1">
              <p className="flex items-center gap-1 text-[13px] font-semibold text-white/95">
                <TrendingUp className="h-4 w-4" /> الهدف الشهري: <span className="font-mono-nums" dir="ltr">{fmt(r.monthly_target)}</span>
              </p>
              <p className="text-[13px] text-white/90">
                المطلوب خلال {r.month_days} يوم: <b className="font-mono-nums" dir="ltr">{fmt(r.expected)}</b>
              </p>
            </div>
          ) : (
            <p className="mt-3 text-[12px] text-white/90">لم يُحدَّد الهدف الشهري بعد (يُضبط من إعدادات الأهداف).</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <div className="rounded-xl border border-slate-200 p-3 text-center">
            <p className="text-[11px] text-slate-400">طلبات هذا اليوم</p>
            <p className="font-heading text-xl font-bold text-slate-800 font-mono-nums">{fmt(r.orders)}</p>
          </div>
          <div className="rounded-xl border border-slate-200 p-3 text-center">
            <p className="text-[11px] text-slate-400">التارقت اليومي</p>
            <p className="font-heading text-xl font-bold text-slate-800 font-mono-nums">{r.target > 0 ? fmt(r.target) : "—"}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <div className="rounded-xl border border-slate-200 p-3 text-center" data-testid="rider-card-hours">
            <div className="flex items-center justify-center gap-1 text-[11px] text-slate-400"><Clock className="h-3.5 w-3.5" /> ساعات الشهر</div>
            <p className="font-heading text-lg font-bold text-slate-800 font-mono-nums mt-0.5">{r.hours != null ? fmt(r.hours) : "—"}</p>
          </div>
          <div className="rounded-xl border border-slate-200 p-3 text-center" data-testid="rider-card-acceptance">
            <div className="flex items-center justify-center gap-1 text-[11px] text-slate-400"><CheckCircle2 className="h-3.5 w-3.5" /> نسبة القبول</div>
            <p className={`font-heading text-lg font-bold font-mono-nums mt-0.5 ${r.acceptance != null && r.acceptance < 90 ? "text-amber-600" : "text-emerald-600"}`}>{r.acceptance != null ? `${fmt(r.acceptance)}%` : "—"}</p>
          </div>
          <div className="rounded-xl border border-slate-200 p-3 text-center" data-testid="rider-card-rejected">
            <div className="flex items-center justify-center gap-1 text-[11px] text-slate-400"><XCircle className="h-3.5 w-3.5" /> المرفوضة</div>
            <p className={`font-heading text-lg font-bold font-mono-nums mt-0.5 ${(r.rejected || 0) > 0 ? "text-rose-600" : "text-slate-800"}`}>{fmt(r.rejected || 0)}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TargetCard = ({ icon: Icon, label, tone, group, value, onSave, onPick }) => {
  const [t, setT] = useState(String(value ?? 0));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setT(String(value ?? 0)); }, [value]);
  const save = async () => { setSaving(true); try { await onSave(parseInt(t, 10) || 0); } finally { setSaving(false); } };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`daily-target-card-${group.key}`}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></div>
          <div>
            <div className="font-heading font-bold text-slate-800 text-sm">التارقت اليومي — {label}</div>
            <div className="text-[11px] text-slate-400">{group.count} مندوب • {group.target > 0 ? `الهدف ${group.target} طلب/يوم` : "لم يُحدَّد الهدف"}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Input type="number" min="0" value={t} onChange={(e) => setT(e.target.value)} data-testid={`daily-target-input-${group.key}`}
            className="h-9 w-20 text-center font-mono-nums" placeholder="الهدف" />
          <Button size="sm" onClick={save} disabled={saving} data-testid={`daily-target-save-${group.key}`} className="h-9 bg-emerald-600 hover:bg-emerald-700 gap-1">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} حفظ
          </Button>
        </div>
      </div>
      <div className="flex gap-3">
        <RankList title="أفضل ١٠" tone="best" rows={group.best} empty="لا بيانات" onPick={onPick} />
        <div className="w-px bg-slate-100" />
        <RankList title="أقل ١٠" tone="worst" rows={group.worst} empty="لا بيانات" onPick={onPick} />
      </div>
    </div>
  );
};

const DailyTargetSection = () => {
  const [platform] = usePlatform();
  const eff = platform === UNIFIED ? "هنجرستيشن" : platform;
  const [dt, setDt] = useState(null);
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const load = (d) => {
    setLoading(true);
    api.ridersDailyTarget(d, eff).then((r) => { setDt(r); setDate(r.date || ""); }).catch(() => toast.error("تعذّر تحميل التارقت اليومي")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [platform]);
  const saveTarget = async (field, val) => {
    try { await api.updateSettings({ [field]: val }); toast.success("تم حفظ الهدف"); load(date); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="daily-target-section">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center"><Trophy className="h-5 w-5 text-amber-500" /></div>
          <div>
            <div className="font-heading font-extrabold text-slate-900">التارقت اليومي المطلوب</div>
            <p className="text-xs text-slate-500">أفضل ١٠ وأقل ١٠ مندوبين حسب طلبات اليوم — منفصلة للسيارات والدبّاب. تتحدّث مع كل تقرير يومي.</p>
          </div>
        </div>
        {dt?.available_dates?.length > 0 && (
          <Select value={date} onValueChange={(v) => { setDate(v); load(v); }}>
            <SelectTrigger data-testid="daily-target-date" className="w-44 h-10 bg-white gap-1"><Calendar className="h-4 w-4 text-slate-400" /><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {dt.available_dates.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !dt?.date ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-400 text-sm">لا توجد تقارير مناديب بعد. ارفع تقرير المنصّة أولًا.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <TargetCard icon={Car} label="السيارات" tone="bg-blue-50 text-blue-600" group={{ ...dt.car, key: "car" }} value={dt.car.target} onSave={(v) => saveTarget("daily_order_target_car", v)} onPick={setPicked} />
          <TargetCard icon={Bike} label="الدبّاب" tone="bg-emerald-50 text-emerald-600" group={{ ...dt.moto, key: "moto" }} value={dt.moto.target} onSave={(v) => saveTarget("daily_order_target_moto", v)} onPick={setPicked} />
        </div>
      )}
      <RiderTargetCardDialog rider={picked} onClose={() => setPicked(null)} />
    </div>
  );
};

export const RidersComparePanel = () => {
  const [platform] = usePlatform();
  const eff = platform === UNIFIED ? "هنجرستيشن" : platform;
  const [riders, setRiders] = useState([]);
  const [riderId, setRiderId] = useState("");
  const [months, setMonths] = useState("6");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const ov = await api.ridersOverview("cumulative", null, null, null, null, null, eff);
        const opts = (ov.rows || [])
          .map((r) => ({ id: r.rider_id, name: r.emp_name || r.rider_name || r.rider_id }))
          .filter((o) => o.id);
        const seen = new Set();
        const uniq = opts.filter((o) => (seen.has(o.id) ? false : seen.add(o.id)));
        uniq.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ar"));
        setRiders(uniq);
        setRiderId(uniq.length ? uniq[0].id : "");
      } catch (e) {
        toast.error("تعذّر تحميل قائمة المناديب");
      } finally { setListLoading(false); }
    })();
  }, [platform]);

  useEffect(() => {
    if (!riderId) return;
    setLoading(true);
    api.ridersCompare(riderId, parseInt(months, 10), eff)
      .then(setData)
      .catch(() => toast.error("تعذّر تحميل بيانات المقارنة"))
      .finally(() => setLoading(false));
  }, [riderId, months, platform]);

  const chartData = useMemo(() => (data?.series || []).map((s) => ({ ...s, m: monthLabel(s.month) })), [data]);
  const last = data?.series?.[data.series.length - 1];
  const prev = data?.series?.[data.series.length - 2];

  return (
    <div className="space-y-4" data-testid="riders-compare-panel">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-l from-slate-50 to-white p-3 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs font-semibold text-slate-500">منصة التوصيل</p>
        <RiderPlatformSwitcher />
      </div>
      <DailyTargetSection />
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-l from-blue-50 to-white p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="h-10 w-10 rounded-xl bg-blue-600/10 flex items-center justify-center"><BarChart3 className="h-5 w-5 text-blue-600" /></div>
          <div>
            <div className="font-heading font-extrabold text-slate-900">مقارنة أداء المندوب</div>
            <p className="text-xs text-slate-500">قارن أداء المندوب عبر الأشهر السابقة: الطلبات، الساعات، الوقود، قطع الغيار.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={riderId} onValueChange={setRiderId} disabled={listLoading || !riders.length}>
            <SelectTrigger data-testid="compare-rider-select" className="flex-1 bg-white h-11">
              <SelectValue placeholder={listLoading ? "جارٍ التحميل…" : "اختر المندوب"} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {riders.map((r) => (<SelectItem key={r.id} value={r.id} data-testid={`compare-rider-opt-${r.id}`}>{r.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger data-testid="compare-months-select" className="sm:w-44 bg-white h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">آخر ٣ أشهر</SelectItem>
              <SelectItem value="6">آخر ٦ أشهر</SelectItem>
              <SelectItem value="12">آخر ١٢ شهرًا</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {data?.vehicle_number && (
          <p className="text-xs text-slate-400 mt-2">المركبة المرتبطة: <span className="font-mono-nums font-semibold text-slate-600">{data.vehicle_number}</span></p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400" data-testid="compare-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !data || !riders.length ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400" data-testid="compare-empty">
          لا توجد بيانات مناديب بعد. ارفع تقرير المنصّة أولًا من «الأداء العام».
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Package} label="إجمالي الطلبات" value={data.totals.orders} unit="طلب" tone="bg-blue-50 text-blue-600" curr={last?.orders} prev={prev?.orders} />
            <StatCard icon={Clock} label="إجمالي الساعات" value={data.totals.hours} unit="س" tone="bg-violet-50 text-violet-600" curr={last?.hours} prev={prev?.hours} />
            <StatCard icon={Fuel} label="استهلاك الوقود" value={data.totals.fuel_sar} unit="⃁" tone="bg-amber-50 text-amber-600" curr={last?.fuel_sar} prev={prev?.fuel_sar} invert />
            <StatCard icon={Wrench} label="استهلاك القطع" value={data.totals.parts_sar} unit="⃁" tone="bg-rose-50 text-rose-600" curr={last?.parts_sar} prev={prev?.parts_sar} invert />
          </div>

          {data.benchmark && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="fuel-efficiency-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"><Gauge className="h-5 w-5" /></div>
                  <div>
                    <div className="font-heading font-extrabold text-slate-900">كفاءة استهلاك الوقود</div>
                    <p className="text-xs text-slate-500">
                      المعيار: <span className="font-semibold text-slate-700">{data.benchmark.label}</span> — {fmt(data.benchmark.orders_per_liter)} طلب/لتر · حد التنبيه ±{fmt(data.benchmark.threshold)}%
                    </p>
                  </div>
                </div>
                <div className="text-end">
                  <div className="text-2xl font-heading font-extrabold text-slate-900 font-mono-nums" data-testid="fuel-efficiency-actual">
                    {fmt(data.benchmark.overall_orders_per_liter)}<span className="text-sm font-bold text-slate-400 mr-1">طلب/لتر فعلي</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {data.benchmark.overall_orders_per_liter >= data.benchmark.orders_per_liter
                      ? <span className="text-emerald-600 font-semibold">كفاءة جيدة ✅</span>
                      : <span className="text-rose-500 font-semibold">أقل من المعيار</span>}
                  </div>
                </div>
              </div>
              {data.alerts?.length > 0 && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="fuel-alerts-box">
                  <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> تغيّرات لافتة في استهلاك الوقود ({data.alerts.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.alerts.map((al) => (
                      <span key={al.month} data-testid={`fuel-alert-${al.month}`}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${al.flag === "over" ? "bg-white text-rose-700 border-rose-300" : "bg-white text-sky-700 border-sky-300"}`}>
                        {monthLabel(al.month)}: {al.flag === "over" ? "هدر" : "أقل من المتوقع"} ({al.dev > 0 ? "+" : ""}{fmt(al.dev)}% · فعلي {fmt(al.actual_liters)}ل / متوقع {fmt(al.expected_liters)}ل)
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ChartCard title="إجمالي الطلبات شهريًا">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="m" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="orders" name="الطلبات" fill="#2563eb" radius={[6, 6, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ChartCard>
            <ChartCard title="ساعات العمل شهريًا">
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="m" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="hours" name="الساعات" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
              </LineChart>
            </ChartCard>
            <ChartCard title="التكاليف شهريًا (وقود مقابل قطع غيار)">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="m" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="fuel_sar" name="وقود ⃁" fill="#f59e0b" radius={[6, 6, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="parts_sar" name="قطع ⃁" fill="#f43f5e" radius={[6, 6, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ChartCard>
            <ChartCard title="نسب القبول والحضور %">
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="m" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="acceptance" name="القبول %" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="attendance" name="الحضور %" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
              </LineChart>
            </ChartCard>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="text-sm font-heading font-bold text-slate-800 px-4 py-3 border-b border-slate-100">جدول المقارنة التفصيلي</div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-right table-fixed" data-testid="compare-table">
                <colgroup>
                  <col className="w-[13%]" />
                  <col span="9" className="w-[9.6%]" />
                </colgroup>
                <thead className="bg-slate-50 text-slate-500 text-xs">
                  <tr>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">الشهر</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">الطلبات</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">الساعات</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">الوقود (ل)</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">المتوقع (ل)</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">الكفاءة</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">الوقود ⃁</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">قطع (عدد)</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">قطع ⃁</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">تكلفة/طلب</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {data.series.map((s) => (
                    <tr key={s.month} className={`border-t border-slate-100 hover:bg-slate-50 ${s.fuel_flag === "over" ? "bg-rose-50/40" : s.fuel_flag === "under" ? "bg-sky-50/40" : ""}`} data-testid={`compare-row-${s.month}`}>
                      <td className="px-3 py-2 font-sans font-semibold text-slate-700 whitespace-nowrap">{monthLabel(s.month)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmt(s.orders)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmt(s.hours)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmt(s.fuel_liters)}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmt(s.expected_liters)}</td>
                      <td className="px-3 py-2 font-sans whitespace-nowrap">{s.orders && s.fuel_liters ? <FlagBadge flag={s.fuel_flag} dev={s.fuel_dev} /> : <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmt(s.fuel_sar)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmt(s.parts_count)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmt(s.parts_sar)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmt(s.cost_per_order)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
